import { supabase } from "./supabase";

// O IO do SindiFácil (000212) — zero regra.
//
// A busca é do SERVIDOR, ao contrário da de ocupações do MEI, que manda as 502
// linhas de uma vez e filtra local. A diferença foi MEDIDA antes de decidir: o
// cadastro sindical inteiro pesa 6,27 MB em JSON enxuto, e só São Paulo pesa
// 1,05 MB (2.627 linhas) — mandar isso ao aluno a cada troca de estado é o
// oposto do `import()` que tirou 387 kB do bundle por causa do `pdfjs`.
//
// Quem decide se pode ler é `sind_buscar`, que pergunta a `abrir_card`. Este
// módulo não decide acesso: ele transporta.

export type Sindicato = {
  id: number;
  cnpj: string;
  /** Nula em 21 linhas da origem — a tela DIZ isso em vez de mostrar vazio. */
  denominacao: string | null;
  /** Nula em 3 linhas cuja origem trazia "-". Não é inferível. */
  uf: string | null;
  categoria: string | null;
};

export type Resultado = {
  total: number;
  pagina: number;
  paginas: number;
  teto: number;
  itens: Sindicato[];
};

export type Busca = {
  uf: string;
  termo: string;
  cnpj: string;
  pagina: number;
};

/** O que a tela manda quando nada foi escolhido. Um lugar só, para o "limpar" e
 *  o estado inicial não divergirem. */
export const BUSCA_VAZIA: Busca = { uf: "", termo: "", cnpj: "", pagina: 1 };

export const POR_PAGINA = 25;

/**
 * A recusa vem do vocabulário de `abrir_card` (`sem-acesso`, `trancado`, …), e
 * quem escreve a frase é a tela — os motivos mandam a lugares diferentes.
 * `uf_invalida` é o único motivo próprio desta função.
 */
export type Falha = { motivo: string };

export async function buscar(b: Busca): Promise<Resultado | Falha | null> {
  const { data, error } = await supabase.rpc("sind_buscar", {
    p_curso: cursoDaQuery(),
    p_uf: b.uf || null,
    p_termo: b.termo.trim() || null,
    p_cnpj: b.cnpj.trim() || null,
    p_limite: POR_PAGINA,
    p_pagina: b.pagina,
  });
  if (error) return null;
  const r = data as { ok?: boolean; motivo?: string } & Resultado;
  if (!r?.ok) return { motivo: r?.motivo ?? "falha" };
  return {
    total: Number(r.total ?? 0),
    pagina: Number(r.pagina ?? 1),
    paginas: Number(r.paginas ?? 1),
    teto: Number(r.teto ?? POR_PAGINA),
    itens: (r.itens ?? []) as Sindicato[],
  };
}

export type Ufs = { ufs: string[]; semUf: number };

export async function listarUfs(): Promise<Ufs | Falha | null> {
  const { data, error } = await supabase.rpc("sind_ufs", { p_curso: cursoDaQuery() });
  if (error) return null;
  const r = data as { ok?: boolean; motivo?: string; ufs?: string[]; sem_uf?: number };
  if (!r?.ok) return { motivo: r?.motivo ?? "falha" };
  return { ufs: r.ufs ?? [], semUf: Number(r.sem_uf ?? 0) };
}

export function ehFalha(r: unknown): r is Falha {
  return typeof r === "object" && r !== null && "motivo" in r;
}

/**
 * O curso pelo qual o card foi aberto, que `abrir_card` precisa saber.
 *
 * Vem do `?curso=` que `aberturaDoCard` põe no link, como em toda ferramenta
 * interna. Hoje o SindiFácil só pertence à Pós DP, então o padrão acerta
 * sempre; o dia em que outra pós ganhar o card, é a query que resolve — e
 * cravar `pos-dp` aqui seria descobrir isso pelo aluno recusado.
 */
function cursoDaQuery(): string {
  const q = new URLSearchParams(window.location.search).get("curso");
  return q && q.trim() !== "" ? q : "pos-dp";
}

/** A máscara do CNPJ. Ela é APRESENTAÇÃO: o banco guarda 14 dígitos, e o que
 *  viaja na busca é o número — a cicatriz do campo de valor do orçamento, onde
 *  o texto mascarado chegou à coluna e virou nulo em silêncio. */
export function cnpjFormatado(d: string): string {
  if (!/^\d{14}$/.test(d)) return d;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}
